import { EVENT_TYPES, type EventType } from './events'

export interface RuntimeMetric {
  id: string
  type: EventType
  timestamp: number
  duration?: number
  success: boolean
  payload?: Record<string, unknown>
}

const runtimeMetrics: RuntimeMetric[] = []
const MAX_RUNTIME_METRICS = 500

export function trackRuntimeMetric(metric: Omit<RuntimeMetric, 'id' | 'timestamp'>) {
  runtimeMetrics.push({
    id: createMetricId(),
    timestamp: Date.now(),
    ...metric,
  })

  if (runtimeMetrics.length > MAX_RUNTIME_METRICS) {
    runtimeMetrics.shift()
  }
}

export async function measureRuntimeMetric<T>(
  type: EventType,
  action: () => Promise<T>,
  payload?: Record<string, unknown>
) {
  const startedAt = Date.now()

  try {
    const result = await action()

    trackRuntimeMetric({
      type,
      success: true,
      duration: Date.now() - startedAt,
      payload,
    })

    return result
  } catch (error) {
    trackRuntimeMetric({
      type: EVENT_TYPES.sincronizacaoErro,
      success: false,
      duration: Date.now() - startedAt,
      payload: {
        ...payload,
        sourceType: type,
        error: error instanceof Error ? error.message : 'Erro desconhecido',
      },
    })

    throw error
  }
}

export function getRuntimeMetrics() {
  return [...runtimeMetrics]
}

export function clearRuntimeMetrics() {
  runtimeMetrics.length = 0
}

export function getRuntimeMetricSummary() {
  const total = runtimeMetrics.length

  const success = runtimeMetrics.filter(metric => metric.success).length
  const error = total - success

  const syncEvents = runtimeMetrics.filter(metric => (
    metric.type === EVENT_TYPES.sincronizacaoConcluida
    || metric.type === EVENT_TYPES.sincronizacaoErro
  ))

  return {
    total,
    success,
    error,
    syncEvents: syncEvents.length,
  }
}

function createMetricId() {
  if ('crypto' in window && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `metric-${Date.now()}-${Math.random().toString(36).slice(2)}`
}
